import React, { useEffect, useRef, useState } from "react";
import logger from "../lib/logger";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Btn, Eyebrow } from "../components/Primitives";
import WheelPicker from "../components/WheelPicker";
import { api, fileUrl } from "../lib/api";
import {
    VALUE_CHIPS, CHILDREN_PLANS, LIFESTYLE_OPTIONS,
    AGE_MIN, AGE_MAX, DISTANCE_MAX_KM, HEIGHT_CM_MIN, HEIGHT_CM_MAX,
    RELATIONSHIP_INTENTS, COMMUNICATION_STYLES, LOVE_LANGUAGES,
    LIFESTYLE_PACE, AMBITION_VIBES, FAITH_OPTIONS, POLITICS_FIT, DEALBREAKER_CHIPS,
} from "../data/profileOptions";
import LivePhotoCapture from "./LivePhotoCapture";
import VideoRecord from "./VideoRecord";

// Module-level constants for Framer Motion props — avoid per-render allocation.
const PROGRESS_TRANSITION = { duration: 0.4 };

// Build picker options
const AGE_OPTIONS = Array.from({ length: AGE_MAX - AGE_MIN + 1 }, (_, i) => {
    const v = AGE_MIN + i;
    return { value: v, label: String(v) };
});
const HEIGHT_OPTIONS = Array.from({ length: HEIGHT_CM_MAX - HEIGHT_CM_MIN + 1 }, (_, i) => {
    const cm = HEIGHT_CM_MIN + i;
    const totalInches = Math.round(cm / 2.54);
    const ft = Math.floor(totalInches / 12);
    const inch = totalInches % 12;
    return { value: cm, label: `${cm} cm  ·  ${ft}′${inch}″` };
});

const STEPS = ["basics", "photos", "live", "video", "preferences", "review"];

export default function ProfileBuilder({ user, archetype, onComplete }) {
    const [stepIdx, setStepIdx] = useState(0);
    const [profile, setProfile] = useState(user?.profile || {});
    const [preferences, setPreferences] = useState(user?.preferences || {});
    const [videoPrompts, setVideoPrompts] = useState([]);
    const [saving, setSaving] = useState(false);
    const [acceptedTerms, setAcceptedTerms] = useState(!!user?.accepted_terms);

    useEffect(() => {
        // Hydrate
        (async () => {
            try {
                const p = await api.getProfile(user.session_id);
                if (p?.profile) setProfile(p.profile);
                if (p?.preferences) setPreferences(p.preferences);
            } catch (err) {
                logger.error("getProfile failed:", err);
            }
            try {
                const r = await api.videoPrompts();
                setVideoPrompts(r.prompts);
            } catch (err) {
                logger.error("videoPrompts failed:", err);
            }
        })();
    }, [user.session_id]);

    const step = STEPS[stepIdx];
    const next = () => setStepIdx(i => Math.min(STEPS.length - 1, i + 1));
    const back = () => setStepIdx(i => Math.max(0, i - 1));

    const saveAll = async () => {
        if (!acceptedTerms) {
            toast.error("Please accept the Terms & Conditions to complete your profile");
            return;
        }
        setSaving(true);
        try {
            await api.saveProfile(user.session_id, { profile, preferences });
            await api.acceptTerms(user.session_id, "profile").catch(err => logger.warn("acceptTerms persist failed:", err));
            toast.success("Profile saved");
            onComplete();
        } catch {
            toast.error("Could not save profile");
        } finally {
            setSaving(false);
        }
    };

    // Render special-screen steps in their own components
    if (step === "live") {
        return (
            <LivePhotoCapture
                existingUrl={profile.live_photo ? fileUrl(profile.live_photo.path) : null}
                onCancel={back}
                onCapture={async (blob) => {
                    try {
                        const res = await api.uploadLivePhoto(user.session_id, blob);
                        setProfile(p => ({ ...p, live_photo: res }));
                        toast.success("Live photo saved");
                        next();
                    } catch {
                        toast.error("Upload failed");
                    }
                }}
            />
        );
    }

    if (step === "video") {
        return (
            <VideoRecord
                prompts={videoPrompts}
                initialPrompt={profile.video?.prompt}
                existingUrl={profile.video ? fileUrl(profile.video.path) : null}
                existingPrompt={profile.video?.prompt}
                onCancel={back}
                onCapture={async (blob, prompt) => {
                    try {
                        const res = await api.uploadVideo(user.session_id, blob, prompt);
                        setProfile(p => ({ ...p, video: res }));
                        toast.success("Video saved");
                        next();
                    } catch {
                        toast.error("Upload failed");
                    }
                }}
            />
        );
    }

    return (
        <div className="flex-1 flex flex-col bg-cream" data-testid="profile-builder">
            {/* Top progress */}
            <div className="w-full h-[2px] bg-burgundy/10">
                <motion.div
                    className="h-full bg-gold"
                    animate={{ width: `${((stepIdx + 1) / STEPS.length) * 100}%` }}
                    transition={PROGRESS_TRANSITION}
                />
            </div>

            <div className="px-6 pt-5 pb-2 flex items-center justify-between">
                <button
                    data-testid="pb-back-btn"
                    disabled={stepIdx === 0}
                    onClick={back}
                    className="tap-target text-[11px] tracking-[0.28em] uppercase text-ink-muted disabled:opacity-30"
                >
                    ← Back
                </button>
                <span className="text-[10px] tracking-[0.32em] uppercase text-ink-muted">
                    {String(stepIdx + 1).padStart(2, "0")} / {String(STEPS.length).padStart(2, "0")}
                </span>
            </div>

            <div className="flex-1 px-7 py-5 overflow-y-auto">
                {step === "basics" && (
                    <Basics profile={profile} setProfile={setProfile} archetype={archetype} />
                )}
                {step === "photos" && (
                    <Photos profile={profile} setProfile={setProfile} user={user} />
                )}
                {step === "preferences" && (
                    <Preferences preferences={preferences} setPreferences={setPreferences} />
                )}
                {step === "review" && (
                    <Review profile={profile} preferences={preferences} />
                )}
            </div>

            <div className="px-6 pb-8 pt-2 pb-safe">
                {step === "review" ? (
                    <>
                        <label className="flex items-start gap-2 cursor-pointer mb-3" data-testid="pb-terms-row">
                            <input
                                type="checkbox"
                                data-testid="pb-terms-checkbox"
                                checked={acceptedTerms}
                                onChange={e => setAcceptedTerms(e.target.checked)}
                                className="mt-0.5 w-5 h-5 shrink-0 accent-burgundy"
                            />
                            <span className="text-xs text-ink leading-snug">
                                I agree to Ever After's{" "}
                                <a href="/terms" target="_blank" rel="noopener" className="text-burgundy underline underline-offset-4">Terms &amp; Conditions</a>
                                {" "}and{" "}
                                <a href="/privacy" target="_blank" rel="noopener" className="text-burgundy underline underline-offset-4">Privacy Policy</a>, and confirm my profile is accurate.
                            </span>
                        </label>
                        <Btn data-testid="pb-finish-btn" onClick={saveAll} disabled={saving || !acceptedTerms}>
                            {saving ? "Saving…" : "Save & join the beta"}
                        </Btn>
                    </>
                ) : (
                    <Btn data-testid="pb-next-btn" onClick={next}>Continue</Btn>
                )}
            </div>
        </div>
    );
}

// --- Step components ---
function FieldRow({ label, children, helper }) {
    return (
        <div className="mb-5">
            <div className="text-[10px] tracking-[0.32em] uppercase text-ink-muted mb-1.5">{label}</div>
            {children}
            {helper && <div className="text-xs text-ink-light mt-1">{helper}</div>}
        </div>
    );
}

function Chips({ options, selected, onToggle, testidPrefix = "chip" }) {
    return (
        <div className="flex flex-wrap gap-2">
            {options.map(opt => {
                const on = selected.includes(opt);
                return (
                    <button
                        key={opt}
                        data-testid={`${testidPrefix}-${opt}`}
                        type="button"
                        onClick={() => onToggle(opt)}
                        className={`px-3 py-1.5 text-xs uppercase tracking-[0.18em] border transition-colors ${
                            on ? "bg-burgundy text-cream border-burgundy" : "border-burgundy/20 text-ink hover:border-burgundy/40"
                        }`}
                    >
                        {opt}
                    </button>
                );
            })}
        </div>
    );
}

function Basics({ profile, setProfile, archetype }) {
    const set = (k, v) => setProfile(p => ({ ...p, [k]: v }));
    const toggleValue = (v) => {
        const cur = profile.values || [];
        set("values", cur.includes(v) ? cur.filter(x => x !== v) : [...cur, v]);
    };

    return (
        <div data-testid="pb-basics">
            <Eyebrow>About you</Eyebrow>
            <h2 className="mt-2 font-display text-3xl text-burgundy leading-tight">
                <span className="italic">The</span> basics.
            </h2>
            {archetype && (
                <p className="mt-1 text-sm text-ink-muted">Your archetype: <span className="font-display italic text-burgundy">{archetype.name}</span></p>
            )}

            <div className="mt-6">
                <FieldRow label="First name">
                    <input
                        data-testid="pb-first-name"
                        value={profile.first_name || ""}
                        onChange={e => set("first_name", e.target.value)}
                        className="w-full bg-transparent border-b border-burgundy/30 focus:border-burgundy py-2 font-display text-2xl text-burgundy focus:outline-none"
                    />
                </FieldRow>

                <div className="grid grid-cols-2 gap-4">
                    <FieldRow label="Age">
                        <WheelPicker
                            testId="pb-age"
                            options={AGE_OPTIONS}
                            value={profile.age ?? null}
                            onChange={(v) => set("age", v)}
                            height={180}
                            itemHeight={40}
                        />
                    </FieldRow>
                    <FieldRow label="Height">
                        <WheelPicker
                            testId="pb-height"
                            options={HEIGHT_OPTIONS}
                            value={profile.height_cm ?? null}
                            onChange={(v) => set("height_cm", v)}
                            height={180}
                            itemHeight={40}
                        />
                    </FieldRow>
                </div>

                <FieldRow label="City / region">
                    <input
                        data-testid="pb-city"
                        value={profile.city || ""}
                        onChange={e => set("city", e.target.value)}
                        className="w-full bg-transparent border-b border-burgundy/30 focus:border-burgundy py-2 font-display text-xl text-burgundy focus:outline-none"
                    />
                </FieldRow>

                <FieldRow label="Occupation">
                    <input
                        data-testid="pb-occupation"
                        value={profile.occupation || ""}
                        onChange={e => set("occupation", e.target.value)}
                        className="w-full bg-transparent border-b border-burgundy/30 focus:border-burgundy py-2 font-display text-xl text-burgundy focus:outline-none"
                    />
                </FieldRow>

                <FieldRow label="Short bio" helper={`${(profile.bio || "").length} / 280`}>
                    <textarea
                        data-testid="pb-bio"
                        maxLength={280}
                        rows={4}
                        value={profile.bio || ""}
                        onChange={e => set("bio", e.target.value)}
                        className="w-full bg-cream border border-burgundy/20 focus:border-burgundy p-3 font-display text-base text-ink focus:outline-none"
                    />
                </FieldRow>

                <FieldRow label="What you're hoping for">
                    <textarea
                        data-testid="pb-hoping-for"
                        rows={3}
                        value={profile.hoping_for || ""}
                        onChange={e => set("hoping_for", e.target.value)}
                        className="w-full bg-cream border border-burgundy/20 focus:border-burgundy p-3 font-display italic text-base text-ink focus:outline-none"
                    />
                </FieldRow>

                <FieldRow label="Values that matter most">
                    <Chips options={VALUE_CHIPS} selected={profile.values || []} onToggle={toggleValue} testidPrefix="pb-value" />
                </FieldRow>
            </div>
        </div>
    );
}

function Photos({ profile, setProfile, user }) {
    const fileRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const photos = profile.photos || [];

    const onPick = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        try {
            const res = await api.uploadPhoto(user.session_id, file);
            setProfile(p => ({ ...p, photos: [...(p.photos || []), res] }));
            toast.success("Photo added");
        } catch (err) {
            toast.error("Upload failed");
        } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    const remove = async (id) => {
        try {
            await api.deletePhoto(user.session_id, id);
            setProfile(p => ({ ...p, photos: (p.photos || []).filter(x => x.id !== id) }));
        } catch {
            toast.error("Could not delete");
        }
    };

    return (
        <div data-testid="pb-photos">
            <Eyebrow>Photos</Eyebrow>
            <h2 className="mt-2 font-display text-3xl text-burgundy leading-tight">
                <span className="italic">Show</span> us a few moments.
            </h2>
            <p className="mt-1 text-sm text-ink-muted">Up to 6. Recent ones, the kind your friends would actually pick.</p>

            <div className="mt-6 grid grid-cols-3 gap-2">
                {photos.map(p => (
                    <div key={p.id} className="relative aspect-square bg-burgundy/5 border border-burgundy/15 overflow-hidden" data-testid={`pb-photo-${p.id}`}>
                        <img src={fileUrl(p.path)} alt="" className="w-full h-full object-cover" />
                        <button
                            data-testid={`pb-photo-remove-${p.id}`}
                            onClick={() => remove(p.id)}
                            className="absolute top-1 right-1 bg-burgundy text-cream text-[9px] tracking-[0.22em] uppercase px-1.5 py-0.5"
                        >
                            Remove
                        </button>
                    </div>
                ))}
                {photos.length < 6 && (
                    <button
                        data-testid="pb-photo-add"
                        onClick={() => fileRef.current?.click()}
                        disabled={uploading}
                        className="aspect-square border border-dashed border-burgundy/30 text-burgundy text-xs tracking-[0.24em] uppercase hover:bg-burgundy/[0.04] disabled:opacity-60"
                    >
                        {uploading ? "Uploading…" : "+ Add"}
                    </button>
                )}
            </div>
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={onPick} className="hidden" data-testid="pb-photo-input" />
        </div>
    );
}

function Preferences({ preferences, setPreferences }) {
    const set = (k, v) => setPreferences(p => ({ ...p, [k]: v }));
    const toggleValue = (v) => {
        const cur = preferences.must_have_values || [];
        set("must_have_values", cur.includes(v) ? cur.filter(x => x !== v) : [...cur, v]);
    };
    const toggleList = (key, v) => {
        const cur = preferences[key] || [];
        set(key, cur.includes(v) ? cur.filter(x => x !== v) : [...cur, v]);
    };
    const setLifestyle = (k, v) => set("lifestyle", { ...(preferences.lifestyle || {}), [k]: v });

    return (
        <div data-testid="pb-preferences">
            <Eyebrow>Your ideal partner</Eyebrow>
            <h2 className="mt-2 font-display text-3xl text-burgundy leading-tight">
                <span className="italic">What</span> are you looking for?
            </h2>
            <p className="mt-2 text-sm text-ink-muted">
                Pick what fits — these aren't filters, they're signals. We'll use them to find people who actually match.
            </p>

            <div className="mt-6">
                <FieldRow label="Relationship intent">
                    <div className="flex flex-wrap gap-2" data-testid="pb-intent">
                        {RELATIONSHIP_INTENTS.map(opt => (
                            <button
                                key={opt.value}
                                data-testid={`pb-intent-${opt.value}`}
                                type="button"
                                onClick={() => set("relationship_intent", opt.value)}
                                className={`px-3 py-1.5 text-xs uppercase tracking-[0.18em] border transition-colors ${
                                    preferences.relationship_intent === opt.value ? "bg-burgundy text-cream border-burgundy" : "border-burgundy/20 text-ink hover:border-burgundy/40"
                                }`}
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>
                </FieldRow>

                <FieldRow label="Communication style (pick up to 3)">
                    <Chips
                        options={COMMUNICATION_STYLES}
                        selected={preferences.communication_styles || []}
                        onToggle={(v) => {
                            const cur = preferences.communication_styles || [];
                            if (cur.includes(v)) toggleList("communication_styles", v);
                            else if (cur.length < 3) toggleList("communication_styles", v);
                        }}
                        testidPrefix="pb-comm"
                    />
                </FieldRow>

                <FieldRow label="Love languages you most need">
                    <Chips
                        options={LOVE_LANGUAGES}
                        selected={preferences.love_languages || []}
                        onToggle={(v) => toggleList("love_languages", v)}
                        testidPrefix="pb-lovelang"
                    />
                </FieldRow>

                <FieldRow label="Lifestyle pace">
                    <Chips
                        options={LIFESTYLE_PACE}
                        selected={preferences.lifestyle_pace || []}
                        onToggle={(v) => toggleList("lifestyle_pace", v)}
                        testidPrefix="pb-pace"
                    />
                </FieldRow>

                <FieldRow label="What drives them">
                    <Chips
                        options={AMBITION_VIBES}
                        selected={preferences.ambition_vibes || []}
                        onToggle={(v) => toggleList("ambition_vibes", v)}
                        testidPrefix="pb-ambition"
                    />
                </FieldRow>

                <FieldRow label="Faith / spirituality">
                    <div className="flex flex-wrap gap-2" data-testid="pb-faith">
                        {FAITH_OPTIONS.map(opt => (
                            <button
                                key={opt.value}
                                data-testid={`pb-faith-${opt.value}`}
                                type="button"
                                onClick={() => set("faith", opt.value)}
                                className={`px-3 py-1.5 text-xs uppercase tracking-[0.18em] border transition-colors ${
                                    preferences.faith === opt.value ? "bg-burgundy text-cream border-burgundy" : "border-burgundy/20 text-ink hover:border-burgundy/40"
                                }`}
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>
                </FieldRow>

                <FieldRow label="Politics fit">
                    <div className="flex flex-wrap gap-2" data-testid="pb-politics">
                        {POLITICS_FIT.map(opt => (
                            <button
                                key={opt.value}
                                data-testid={`pb-politics-${opt.value}`}
                                type="button"
                                onClick={() => set("politics_fit", opt.value)}
                                className={`px-3 py-1.5 text-xs uppercase tracking-[0.18em] border transition-colors ${
                                    preferences.politics_fit === opt.value ? "bg-burgundy text-cream border-burgundy" : "border-burgundy/20 text-ink hover:border-burgundy/40"
                                }`}
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>
                </FieldRow>

                <FieldRow label="Age range">
                    <div className="flex items-center gap-3">
                        <input
                            data-testid="pb-age-min"
                            type="number"
                            min={AGE_MIN}
                            max={AGE_MAX}
                            value={preferences.age_min ?? ""}
                            placeholder="From"
                            onChange={e => set("age_min", e.target.value ? parseInt(e.target.value, 10) : null)}
                            className="w-24 bg-transparent border-b border-burgundy/30 focus:border-burgundy py-2 font-display text-xl text-burgundy focus:outline-none"
                        />
                        <span className="text-ink-muted">to</span>
                        <input
                            data-testid="pb-age-max"
                            type="number"
                            min={AGE_MIN}
                            max={AGE_MAX}
                            value={preferences.age_max ?? ""}
                            placeholder="To"
                            onChange={e => set("age_max", e.target.value ? parseInt(e.target.value, 10) : null)}
                            className="w-24 bg-transparent border-b border-burgundy/30 focus:border-burgundy py-2 font-display text-xl text-burgundy focus:outline-none"
                        />
                    </div>
                </FieldRow>

                <FieldRow label={`Distance: ${preferences.distance_max ?? 50} km`}>
                    <input
                        data-testid="pb-distance"
                        type="range"
                        min={5}
                        max={DISTANCE_MAX_KM}
                        step={5}
                        value={preferences.distance_max ?? 50}
                        onChange={e => set("distance_max", parseInt(e.target.value, 10))}
                        className="w-full accent-burgundy"
                    />
                </FieldRow>

                <FieldRow label="Height range (cm)">
                    <div className="flex items-center gap-3">
                        <input
                            data-testid="pb-height-min"
                            type="number"
                            min={HEIGHT_CM_MIN}
                            max={HEIGHT_CM_MAX}
                            value={preferences.height_min_cm ?? ""}
                            placeholder="From"
                            onChange={e => set("height_min_cm", e.target.value ? parseInt(e.target.value, 10) : null)}
                            className="w-24 bg-transparent border-b border-burgundy/30 focus:border-burgundy py-2 font-display text-xl text-burgundy focus:outline-none"
                        />
                        <span className="text-ink-muted">to</span>
                        <input
                            data-testid="pb-height-max"
                            type="number"
                            min={HEIGHT_CM_MIN}
                            max={HEIGHT_CM_MAX}
                            value={preferences.height_max_cm ?? ""}
                            placeholder="To"
                            onChange={e => set("height_max_cm", e.target.value ? parseInt(e.target.value, 10) : null)}
                            className="w-24 bg-transparent border-b border-burgundy/30 focus:border-burgundy py-2 font-display text-xl text-burgundy focus:outline-none"
                        />
                    </div>
                </FieldRow>

                <FieldRow label="Must-have values">
                    <Chips options={VALUE_CHIPS} selected={preferences.must_have_values || []} onToggle={toggleValue} testidPrefix="pb-pref-value" />
                </FieldRow>

                <FieldRow label="Children plans">
                    <div className="flex flex-wrap gap-2" data-testid="pb-children">
                        {CHILDREN_PLANS.map(opt => (
                            <button
                                key={opt.value}
                                data-testid={`pb-children-${opt.value}`}
                                type="button"
                                onClick={() => set("children_plans", opt.value)}
                                className={`px-3 py-1.5 text-xs uppercase tracking-[0.18em] border transition-colors ${
                                    preferences.children_plans === opt.value ? "bg-burgundy text-cream border-burgundy" : "border-burgundy/20 text-ink hover:border-burgundy/40"
                                }`}
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>
                </FieldRow>

                <FieldRow label="Lifestyle">
                    {Object.entries(LIFESTYLE_OPTIONS).map(([k, options]) => (
                        <div key={k} className="mt-2">
                            <div className="text-[10px] tracking-[0.28em] uppercase text-ink-light mb-1">{k}</div>
                            <div className="flex flex-wrap gap-2" data-testid={`pb-lifestyle-${k}`}>
                                {options.map(opt => {
                                    const on = preferences.lifestyle?.[k] === opt;
                                    return (
                                        <button
                                            key={opt}
                                            data-testid={`pb-lifestyle-${k}-${opt}`}
                                            type="button"
                                            onClick={() => setLifestyle(k, opt)}
                                            className={`px-3 py-1 text-xs uppercase tracking-[0.18em] border transition-colors ${
                                                on ? "bg-burgundy text-cream border-burgundy" : "border-burgundy/20 text-ink hover:border-burgundy/40"
                                            }`}
                                        >
                                            {opt}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </FieldRow>

                <FieldRow label="Deal-breakers (tap any that apply)">
                    <Chips
                        options={DEALBREAKER_CHIPS}
                        selected={preferences.dealbreaker_chips || []}
                        onToggle={(v) => toggleList("dealbreaker_chips", v)}
                        testidPrefix="pb-db"
                    />
                </FieldRow>

                <FieldRow label="Anything else (optional)">
                    <textarea
                        data-testid="pb-dealbreakers"
                        rows={3}
                        value={preferences.dealbreakers || ""}
                        onChange={e => set("dealbreakers", e.target.value)}
                        placeholder="Optional — anything we missed"
                        className="w-full bg-cream border border-burgundy/20 focus:border-burgundy p-3 font-display italic text-base text-ink focus:outline-none"
                    />
                </FieldRow>
            </div>
        </div>
    );
}

function Review({ profile, preferences }) {
    return (
        <div data-testid="pb-review">
            <Eyebrow>Review</Eyebrow>
            <h2 className="mt-2 font-display text-3xl text-burgundy leading-tight">
                <span className="italic">Looks</span> right?
            </h2>
            <div className="mt-5 space-y-4">
                <div className="border border-burgundy/15 p-4 bg-cream-soft">
                    <div className="text-[10px] tracking-[0.32em] uppercase text-gold-deep">You</div>
                    <div className="mt-1 font-display text-xl text-burgundy">
                        {profile.first_name || "—"}{profile.age ? `, ${profile.age}` : ""}
                    </div>
                    <div className="text-sm text-ink-muted">{profile.city} {profile.occupation && `· ${profile.occupation}`}</div>
                    {profile.bio && <p className="mt-2 text-sm text-ink leading-relaxed">{profile.bio}</p>}
                    {(profile.values || []).length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                            {(profile.values || []).map(v => <span key={v} className="text-[10px] tracking-[0.22em] uppercase bg-burgundy/[0.06] text-burgundy px-2 py-0.5">{v}</span>)}
                        </div>
                    )}
                </div>

                <div className="border border-burgundy/15 p-4 bg-cream-soft">
                    <div className="text-[10px] tracking-[0.32em] uppercase text-gold-deep">Media</div>
                    <div className="mt-2 grid grid-cols-2 gap-2">
                        {profile.live_photo ? (
                            <img src={fileUrl(profile.live_photo.path)} alt="Live" className="aspect-square object-cover border border-burgundy/15" data-testid="pb-review-live" />
                        ) : <div className="aspect-square bg-burgundy/5 border border-dashed border-burgundy/20 flex items-center justify-center text-xs text-ink-light">No live photo</div>}
                        {profile.video ? (
                            <video src={fileUrl(profile.video.path)} controls className="aspect-square object-cover border border-burgundy/15" data-testid="pb-review-video" />
                        ) : <div className="aspect-square bg-burgundy/5 border border-dashed border-burgundy/20 flex items-center justify-center text-xs text-ink-light">No video</div>}
                    </div>
                </div>

                <div className="border border-burgundy/15 p-4 bg-cream-soft">
                    <div className="text-[10px] tracking-[0.32em] uppercase text-gold-deep">Your ideal partner</div>
                    <p className="mt-1 text-sm text-ink-muted">
                        {preferences.age_min || "?"} – {preferences.age_max || "?"} years ·
                        within {preferences.distance_max || "?"} km ·
                        {preferences.children_plans ? ` ${preferences.children_plans} children` : ""}
                    </p>
                    {(preferences.must_have_values || []).length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                            {(preferences.must_have_values || []).map(v => <span key={v} className="text-[10px] tracking-[0.22em] uppercase bg-gold/30 text-burgundy px-2 py-0.5">{v}</span>)}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
